import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

/**
 * Full view of what the signed-in learner owns: settings, certificates,
 * all submissions with their portfolio_visible flag, and competencies.
 * Powers the dashboard Portfolio tab and the JSON export.
 */
export const getMyVerifiedWork = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabase, userId } = context;
    const [settingsQ, certsQ, subsQ, compsQ] = await Promise.all([
      supabase.from("lxp_portfolio_settings").select("*").eq("user_id", userId).maybeSingle(),
      supabase.from("lxp_certificates")
        .select("id,certificate_number,verification_token,issued_at,final_score,full_name,simulation:lxp_simulations(title,practice_area,slug)")
        .eq("user_id", userId)
        .order("issued_at", { ascending: false }),
      supabase.from("lxp_task_submissions")
        .select("id,task_index,portfolio_visible,ai_scores,supervisor_narrative,submitted_at,body_html,run:lxp_simulation_runs(simulation:lxp_simulations(title,slug,practice_area,tasks))")
        .eq("user_id", userId)
        .order("submitted_at", { ascending: false, nullsFirst: false }),
      supabase.from("lxp_competencies").select("competency_key,competency_label,xp,level").eq("user_id", userId).order("xp", { ascending: false }),
    ]);
    const certificates = (certsQ.data ?? []).map((c: any) => ({
      id: c.id,
      certificate_number: c.certificate_number,
      verification_token: c.verification_token,
      issued_at: c.issued_at,
      final_score: c.final_score,
      full_name: c.full_name,
      simulation_title: c.simulation?.title ?? "",
      practice_area: c.simulation?.practice_area ?? "",
      simulation_slug: c.simulation?.slug ?? "",
    }));
    const submissions = (subsQ.data ?? []).map((s: any) => ({
      id: s.id,
      task_index: s.task_index,
      task_title: s.run?.simulation?.tasks?.[s.task_index]?.title ?? `Task ${s.task_index + 1}`,
      simulation_title: s.run?.simulation?.title ?? "",
      simulation_slug: s.run?.simulation?.slug ?? "",
      practice_area: s.run?.simulation?.practice_area ?? "",
      portfolio_visible: !!s.portfolio_visible,
      ai_scores: s.ai_scores,
      supervisor_narrative: s.supervisor_narrative,
      submitted_at: s.submitted_at,
      body_html: s.body_html,
    }));
    return {
      settings: settingsQ.data ?? null,
      certificates,
      submissions,
      competencies: compsQ.data ?? [],
    };
  });
